import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Truck, Package, Archive, CheckCircle, Loader2 } from "lucide-react";
import { API } from "@/App";
import { PostcodeLookup } from "@/components/PostcodeLookup";

/**
 * Reusable Removals Quote Modal Component
 * Used in SolicitorDashboard and CaseHubPage
 */
export function RemovalsQuoteModal({ 
  open, 
  onOpenChange, 
  user = {}, 
  cases = [],
  showLinkedProperty = true 
}) {
  const token = localStorage.getItem("token");
  
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    linkedProperty: "none",
    moveFrom: "",
    moveTo: "",
    moveFromPostcode: "",
    moveToPostcode: "",
    propertyType: "",
    bedrooms: "",
    hasGarage: false,
    hasGarden: false,
    hasLoft: false,
    hasConservatory: false,
    floorLevel: "ground",
    serviceType: "",
    needsStorage: "",
    preferredDate: "",
    name: "",
    email: "",
    phone: "",
    additionalNotes: ""
  });
  const [instantQuote, setInstantQuote] = useState(null);
  const [calculatingQuote, setCalculatingQuote] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setQuoteForm({
        linkedProperty: "none",
        moveFrom: "",
        moveTo: "",
        moveFromPostcode: "",
        moveToPostcode: "",
        propertyType: "",
        bedrooms: "",
        hasGarage: false,
        hasGarden: false,
        hasLoft: false,
        hasConservatory: false,
        floorLevel: "ground",
        serviceType: "",
        needsStorage: "",
        preferredDate: "",
        name: user.name || "",
        email: user.email || "",
        phone: "",
        additionalNotes: ""
      });
      setInstantQuote(null);
    }
  }, [open, user]);

  // Calculate instant quote based on H&E pricing
  const calculateInstantQuote = () => {
    const { bedrooms, propertyType, serviceType, hasGarage, hasGarden, hasLoft, hasConservatory, floorLevel, needsStorage, moveFromPostcode, moveToPostcode } = quoteForm;
    
    if (!bedrooms || !propertyType || !serviceType) return;
    
    setCalculatingQuote(true);
    
    // Base pricing by bedrooms (Move Only)
    const basePrices = { "1": 450, "2": 575, "3": 775, "4": 1100, "5": 1450 };
    let price = basePrices[bedrooms] || 775;
    
    // Property type adjustments
    const flatTypes = ["studio", "flat", "maisonette"];
    const smallerTypes = ["bungalow", "terraced", "end_terrace"];
    const largerTypes = ["detached", "farm_house"];
    
    if (flatTypes.includes(propertyType)) price -= 100;
    else if (smallerTypes.includes(propertyType)) price -= 50;
    else if (largerTypes.includes(propertyType)) price += 100;
    
    // Floor level for flats/maisonettes
    if (flatTypes.includes(propertyType)) {
      const floorPrices = { "ground": 0, "1": 75, "2": 125, "3": 175, "4+": 225 };
      price += floorPrices[floorLevel] || 0;
    }
    
    // Extras
    if (hasGarage) price += 200;
    if (hasGarden) price += 150;
    if (hasLoft) price += 100;
    if (hasConservatory) price += 75;
    
    // Storage
    const storageWeekly = 50;
    let storageEstimate = 0;
    if (needsStorage === "short_term") storageEstimate = storageWeekly * 4;
    else if (needsStorage === "long_term") storageEstimate = storageWeekly * 8;
    
    // Distance estimate
    let distanceNote = "Local move (Bedford area)";
    let distanceSurcharge = 0;
    
    if (moveFromPostcode && moveToPostcode) {
      const fromPrefix = moveFromPostcode.toUpperCase().slice(0, 2);
      const toPrefix = moveToPostcode.toUpperCase().slice(0, 2);
      const bedfordPrefixes = ["MK", "SG", "NN", "PE"];
      const isFromLocal = bedfordPrefixes.some(p => fromPrefix.startsWith(p));
      const isToLocal = bedfordPrefixes.some(p => toPrefix.startsWith(p));
      
      if (!isFromLocal || !isToLocal) {
        distanceNote = "Long distance move - price may vary";
        distanceSurcharge = 300;
        price += distanceSurcharge;
      }
    }
    
    const needsPackingDiscussion = ["packing_and_moving", "packing_only", "part_packing", "unpacking"].includes(serviceType);
    
    setTimeout(() => {
      setInstantQuote({
        basePrice: basePrices[bedrooms] || 775,
        adjustments: {
          propertyType: flatTypes.includes(propertyType) ? -100 : smallerTypes.includes(propertyType) ? -50 : largerTypes.includes(propertyType) ? 100 : 0,
          floorLevel: flatTypes.includes(propertyType) ? ({ "ground": 0, "1": 75, "2": 125, "3": 175, "4+": 225 }[floorLevel] || 0) : 0,
          garage: hasGarage ? 200 : 0,
          garden: hasGarden ? 150 : 0,
          loft: hasLoft ? 100 : 0,
          conservatory: hasConservatory ? 75 : 0,
          distance: distanceSurcharge
        },
        storage: storageEstimate,
        distanceNote,
        total: price,
        totalWithStorage: price + storageEstimate,
        needsPackingDiscussion,
        isEstimate: true
      });
      setCalculatingQuote(false);
    }, 500);
  };

  // Recalculate when form changes
  useEffect(() => {
    if (open && quoteForm.bedrooms && quoteForm.propertyType && quoteForm.serviceType) {
      calculateInstantQuote();
    }
  }, [open, quoteForm.bedrooms, quoteForm.propertyType, quoteForm.serviceType, quoteForm.hasGarage, quoteForm.hasGarden, quoteForm.hasLoft, quoteForm.hasConservatory, quoteForm.floorLevel, quoteForm.needsStorage, quoteForm.moveFromPostcode, quoteForm.moveToPostcode]);

  const handleLinkedPropertyChange = (value) => {
    let moveFrom = "";
    let moveTo = "";
    let moveFromPostcode = "";
    let moveToPostcode = "";
    let bedrooms = "";
    let propertyType = "";
    
    const saleCase = cases.find(c => c.acting_for === "seller");
    const purchaseCase = cases.find(c => c.acting_for === "buyer");
    
    if (value === "sale" && saleCase) {
      moveFrom = saleCase.address_line1 || "";
      moveFromPostcode = saleCase.postcode || "";
      bedrooms = saleCase.bedrooms?.toString() || "";
      propertyType = saleCase.property_type?.toLowerCase() || "";
    } else if (value === "purchase" && purchaseCase) {
      moveTo = purchaseCase.address_line1 || "";
      moveToPostcode = purchaseCase.postcode || "";
      bedrooms = purchaseCase.bedrooms?.toString() || "";
      propertyType = purchaseCase.property_type?.toLowerCase() || "";
    } else if (value === "both") {
      if (saleCase) {
        moveFrom = saleCase.address_line1 || "";
        moveFromPostcode = saleCase.postcode || "";
        bedrooms = saleCase.bedrooms?.toString() || "";
        propertyType = saleCase.property_type?.toLowerCase() || "";
      }
      if (purchaseCase) {
        moveTo = purchaseCase.address_line1 || "";
        moveToPostcode = purchaseCase.postcode || "";
      }
    }
    
    setQuoteForm(prev => ({ 
      ...prev, 
      linkedProperty: value, 
      moveFrom, 
      moveTo,
      moveFromPostcode,
      moveToPostcode,
      bedrooms: bedrooms || prev.bedrooms,
      propertyType: propertyType || prev.propertyType
    }));
  };

  const handleSubmitQuote = async () => {
    if (!quoteForm.serviceType || !quoteForm.name || !quoteForm.email || !quoteForm.phone) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setQuoteSubmitting(true);
    try {
      await axios.post(`${API}/removals/quote`, {
        ...quoteForm,
        instantQuote: instantQuote
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Quote request sent! H&E Removals will be in touch shortly.");
      onOpenChange(false);
    } catch (error) {
      toast.error("Failed to send quote request");
    } finally {
      setQuoteSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-emerald-600" />
            Request a Free Removal Quote
          </DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-2">
          {/* Left Column - Form */}
          <div className="space-y-4">
            {/* Linked Property Question */}
            {showLinkedProperty && cases.length > 0 && (
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <Label className="text-sm font-medium text-blue-900">
                  Link to your case?
                </Label>
                <Select 
                  value={quoteForm.linkedProperty} 
                  onValueChange={handleLinkedPropertyChange}
                >
                  <SelectTrigger className="mt-2 bg-white">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sale">Selling (move FROM)</SelectItem>
                    <SelectItem value="purchase">Buying (move TO)</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                    <SelectItem value="none">Not linked</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Addresses with Postcodes */}
            <div className="space-y-3">
              <div>
                <Label className="font-medium">Moving From</Label>
                <PostcodeLookup
                  className="mt-1"
                  onAddressSelect={(addr) => {
                    setQuoteForm(prev => ({
                      ...prev,
                      moveFrom: addr.line_1 + (addr.line_2 ? ", " + addr.line_2 : "") + ", " + addr.town,
                      moveFromPostcode: addr.postcode
                    }));
                  }}
                />
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <Input
                    placeholder="Address"
                    value={quoteForm.moveFrom}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, moveFrom: e.target.value }))}
                    className="col-span-2"
                  />
                  <Input
                    placeholder="Postcode"
                    value={quoteForm.moveFromPostcode}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, moveFromPostcode: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label className="font-medium">Moving To</Label>
                <PostcodeLookup
                  className="mt-1"
                  onAddressSelect={(addr) => {
                    setQuoteForm(prev => ({
                      ...prev,
                      moveTo: addr.line_1 + (addr.line_2 ? ", " + addr.line_2 : "") + ", " + addr.town,
                      moveToPostcode: addr.postcode
                    }));
                  }}
                />
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <Input
                    placeholder="Address"
                    value={quoteForm.moveTo}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, moveTo: e.target.value }))}
                    className="col-span-2"
                  />
                  <Input
                    placeholder="Postcode"
                    value={quoteForm.moveToPostcode}
                    onChange={(e) => setQuoteForm(prev => ({ ...prev, moveToPostcode: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Property Details */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Property Type *</Label>
                <Select 
                  value={quoteForm.propertyType} 
                  onValueChange={(v) => setQuoteForm(prev => ({ ...prev, propertyType: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="studio">Studio</SelectItem>
                    <SelectItem value="flat">Flat/Apartment</SelectItem>
                    <SelectItem value="maisonette">Maisonette</SelectItem>
                    <SelectItem value="bungalow">Bungalow</SelectItem>
                    <SelectItem value="terraced">Terraced House</SelectItem>
                    <SelectItem value="end_terrace">End Terrace</SelectItem>
                    <SelectItem value="semi_detached">Semi-Detached</SelectItem>
                    <SelectItem value="detached">Detached</SelectItem>
                    <SelectItem value="town_house">Town House</SelectItem>
                    <SelectItem value="cottage">Cottage</SelectItem>
                    <SelectItem value="farm_house">Farm House</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Bedrooms *</Label>
                <Select 
                  value={quoteForm.bedrooms} 
                  onValueChange={(v) => setQuoteForm(prev => ({ ...prev, bedrooms: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 bedroom</SelectItem>
                    <SelectItem value="2">2 bedrooms</SelectItem>
                    <SelectItem value="3">3 bedrooms</SelectItem>
                    <SelectItem value="4">4 bedrooms</SelectItem>
                    <SelectItem value="5">5+ bedrooms</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Floor Level for Flats/Maisonettes */}
            {["studio", "flat", "maisonette"].includes(quoteForm.propertyType) && (
              <div>
                <Label>Floor Level</Label>
                <Select 
                  value={quoteForm.floorLevel} 
                  onValueChange={(v) => setQuoteForm(prev => ({ ...prev, floorLevel: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ground">Ground floor</SelectItem>
                    <SelectItem value="1">1st floor</SelectItem>
                    <SelectItem value="2">2nd floor</SelectItem>
                    <SelectItem value="3">3rd floor</SelectItem>
                    <SelectItem value="4+">4th floor+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Extras */}
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={quoteForm.hasGarage}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, hasGarage: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm">Garage</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={quoteForm.hasGarden}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, hasGarden: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm">Garden/Shed</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={quoteForm.hasLoft}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, hasLoft: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm">Loft</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={quoteForm.hasConservatory}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, hasConservatory: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm">Conservatory</span>
              </label>
            </div>

            {/* Service Type */}
            <div>
              <Label className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                Service Required *
              </Label>
              <Select 
                value={quoteForm.serviceType} 
                onValueChange={(v) => setQuoteForm(prev => ({ ...prev, serviceType: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select service type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="move_only">Move Only (Loading & Unloading)</SelectItem>
                  <SelectItem value="packing_and_moving">Full Packing & Moving Service</SelectItem>
                  <SelectItem value="packing_only">Packing Service Only</SelectItem>
                  <SelectItem value="unpacking">Unpacking Service</SelectItem>
                  <SelectItem value="part_packing">Part Packing (Fragiles Only)</SelectItem>
                  <SelectItem value="furniture_assembly">Furniture Disassembly/Assembly</SelectItem>
                  <SelectItem value="piano_move">Piano/Heavy Item Move</SelectItem>
                  <SelectItem value="office_move">Office/Commercial Move</SelectItem>
                  <SelectItem value="student_move">Student Move</SelectItem>
                  <SelectItem value="single_item">Single Item Delivery</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Storage */}
            <div>
              <Label className="flex items-center gap-2">
                <Archive className="w-4 h-4" />
                Storage needed?
              </Label>
              <Select 
                value={quoteForm.needsStorage} 
                onValueChange={(v) => setQuoteForm(prev => ({ ...prev, needsStorage: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No storage</SelectItem>
                  <SelectItem value="short_term">Short term (up to 1 month)</SelectItem>
                  <SelectItem value="long_term">Long term (1 month+)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Right Column - Quote Info */}
          <div className="space-y-4">
            {/* Quote Info Box */}
            <div className="rounded-xl p-4 border-2 bg-emerald-50 border-emerald-200">
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <Truck className="w-5 h-5 text-emerald-600" />
                Get Your Free Quote
              </h3>
              
              <div className="space-y-3">
                <p className="text-sm text-slate-700">
                  Fill in your move details and we&apos;ll send you a personalised quote straight away.
                </p>
                
                <div className="bg-white rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                    <span>No obligation quote</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                    <span>Competitive local rates</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                    <span>Fully insured & professional</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                    <span>Packing services available</span>
                  </div>
                </div>
                
                <p className="text-xs text-slate-500 mt-2">
                  Your quote will be emailed to you directly.
                </p>
              </div>
            </div>

            {/* Contact Details */}
            <div className="space-y-3">
              <p className="text-sm font-medium">Your Details</p>
              <Input
                placeholder="Name *"
                value={quoteForm.name}
                onChange={(e) => setQuoteForm(prev => ({ ...prev, name: e.target.value }))}
              />
              <Input
                type="email"
                placeholder="Email *"
                value={quoteForm.email}
                onChange={(e) => setQuoteForm(prev => ({ ...prev, email: e.target.value }))}
              />
              <Input
                type="tel"
                placeholder="Phone *"
                value={quoteForm.phone}
                onChange={(e) => setQuoteForm(prev => ({ ...prev, phone: e.target.value }))}
              />
            </div>

            {/* Additional Notes */}
            <Textarea
              placeholder="Any special requirements?"
              value={quoteForm.additionalNotes}
              onChange={(e) => setQuoteForm(prev => ({ ...prev, additionalNotes: e.target.value }))}
              rows={2}
            />

            {/* Submit */}
            <Button 
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              onClick={handleSubmitQuote}
              disabled={quoteSubmitting || !quoteForm.serviceType || !quoteForm.propertyType || !quoteForm.bedrooms}
            >
              {quoteSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Truck className="w-4 h-4 mr-2" />
              )}
              Get My Free Quote
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              We&apos;ll email your personalised quote straight away
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default RemovalsQuoteModal;
